// Hideo - content types and statuses.
// FIXED content types (user request: with user-added types the app didn't know which fields to show). Each has its
// "kind", which decides the dialog fields and the second tab: 'h' Long Form (YouTube SEO) · 'v' Short Form (vertical:
// hook, length, caption) · 'post' Post (image and caption, no script) · 'live' Live (run sheet, time, title and
// description) · 'blog' Blog (article in the editor, web SEO). They are never added to or renamed; the names are the same
// in every language.
import { t, tv } from '../i18n'
import type { FormatName, Kind, StatusKey, Video } from './types'

export const VIDEO_TYPES: readonly (readonly [FormatName, Kind])[] = [['Long Form', 'h'], ['Short Form', 'v'], ['Post', 'post'], ['Live', 'live'], ['Blog', 'blog']]
export const DEF_FORMATS: FormatName[] = VIDEO_TYPES.map(type => type[0])

// Statuses: the data keeps the original five (the Board columns, stored values that never change), but each type
// names them in its own words. statusName(kind, status) → name to show, in the UI language.
export const STATUS_KEYS: readonly StatusKey[] = ['Idea', 'In Script', 'In Registrazione', 'In Montaggio', 'Pronto/Pubblicato']
/** The last status: ready or published (stored value, never translated) */
export const STATUS_READY: StatusKey = 'Pronto/Pubblicato'
/** Long Form and Short Form */
const STATUS_LABELS = [t('Idea'), t('Scripting'), t('Recording'), t('Editing'), t('Ready/Published')]
export const STATUS_NAMES: Partial<Record<Kind, string[]>> = {
  post: [t('Idea'), t('Preparing'), t('Creating'), t('Reviewing'), t('Ready/Published')],
  blog: [t('Idea'), t('Writing'), t('Writing'), t('Writing'), t('Ready')],
  // "Ready to go" and not "Ready": the Italian is feminine here ("Pronta", la live) and a key has one translation
  live: [t('Idea'), t('Planning'), t('Tech check'), t('Announcement'), t('Ready to go')]
}
// Types with fewer steps (user request, names chosen with the user): Blog only uses Idea → Writing (= "In Script") → Ready.
// Unused statuses disappear from the dialog and from the filtered Board columns; an unused status becomes the closest one.
const STATUS_USED: Partial<Record<Kind, StatusKey[]>> = { blog: ['Idea', 'In Script', 'Pronto/Pubblicato'] }
export const kindStatuses = (kind: Kind): readonly StatusKey[] => STATUS_USED[kind] || STATUS_KEYS
export const fixStatus = (kind: Kind, s: string): string => {
  const used = kindStatuses(kind)
  if ((used as readonly string[]).includes(s)) return s
  const i = (STATUS_KEYS as readonly string[]).indexOf(s)
  return i < 0 ? s : [...used].reverse().find(x => STATUS_KEYS.indexOf(x) <= i) || used[0]
}
/** Neutral names of the Board columns when different types are shown together (ped.js, boardColName) */
export const STATUS_NEUTRAL = [t('Idea'), t('Preparation'), t('Production'), t('Finishing'), t('Ready')]
export const statusName = (kind: Kind, s: string | undefined): string => {
  const i = (STATUS_KEYS as readonly string[]).indexOf(s || '')
  return i >= 0 ? (STATUS_NAMES[kind] || STATUS_LABELS)[i] : (s ? String(tv(s)) : STATUS_LABELS[0])
}
export const videoStatusName = (v: Pick<Video, 'format' | 'status'>): string => statusName(formatKind(v.format), v.status)

/** Old per-type orientation chosen by the user, from old saves (settings.formatKinds) */
export type FormatKinds = Record<string, string>
// Any type name (also from old saves, with the orientation chosen then in formatKinds) → one of the five fixed ones.
// The names were typed by the user: Italian and English words are both recognised.
export function fixedType(name: unknown, fk: FormatKinds = {}): FormatName {
  const n = String(name || '').trim()
  const same = DEF_FORMATS.find(f => f.toLowerCase() === n.toLowerCase())
  if (same) return same
  if (/blog|articolo|article|newsletter/i.test(n)) return 'Blog'
  if (fk[n] === 'live' || /live|diretta|stream/i.test(n)) return 'Live'
  if (/post|carosello|carousel|foto|photo|immagine|image|grafica|graphic/i.test(n)) return 'Post'
  if (fk[n] === 'v' || /short|reel|tiktok|verticale|vertical|storie|stor(y|ies)/i.test(n)) return 'Short Form'
  return 'Long Form'
}
/** Kind of a type name (the legacy code passed the data first: tests/golden adapt it) */
export const formatKind = (name: unknown): Kind => (VIDEO_TYPES.find(type => type[0] === fixedType(name)) || VIDEO_TYPES[0])[1]
